import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiRadioboxBlank,
  mdiRadioboxMarked,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useOrderCreateCtx } from "../../../../../../context_providers/order_create_context";
import {
  ToastType,
  useToast,
} from "../../../../../../context_providers/toast/toast_ctx";
import { CustomerDetailModel } from "../../../../../../service/models/orders/customer_detail_model";
import { GetBillingProfilesOfUserAndTypeApi } from "../../../../../../service/repos/customer/billing_profile_repo";
import ModalDialog from "../../../../../new_components/common/modal";
import AddBillingProfileWidget from "./add_billing_profile_widget";

interface NewBillingProfileWidgetProps {}

const NewBillingProfileWidget: FunctionComponent<
  NewBillingProfileWidgetProps
> = () => {
  const { showToast } = useToast();
  const { user, billing, setBilling, deliveryProfile } = useOrderCreateCtx();
  const [same, setSame] = useState(true);
  const [addNew, setAddNew] = useState(false);
  const [edit, setEdit] = useState(false);
  const [customers, setCustomers] = useState<CustomerDetailModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const getCustomers = async () => {
    setLoading(true);
    const res = await GetBillingProfilesOfUserAndTypeApi(user?.uid, "Billing");

    if (res.success) {
      setCustomers(res.data.billing_profiles);
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  useEffect(() => {
    getCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (billing && !customers.some((c) => c.id === billing.id)) {
      setCustomers((o) => [...o, billing]);
    }
  }, [billing]);

  // Billing is a single address per customer — auto-select it.
  useEffect(() => {
    if (customers.length && !billing) {
      setBilling(customers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  const l = page * 2 - 1;
  const h = l + 2 - 1;

  const gstNumber = user?.gst_details?.gst_number ?? user?.gst ?? "--";
  const gstDetails = user?.gst_details?.gst_registration ?? "--";
  const customerGroup =
    user?.additional_customer_details?.customer_group ?? "--";
  const customerType = user?.additional_customer_details?.customer_type ?? "--";

  return (
    <>
      {addNew && (
        <ModalDialog
          id="order-create-billing-add-modal"
          title="Add Billing Address"
          show={addNew}
          onClose={() => setAddNew(false)}
        >
          <div className="p-5">
            <AddBillingProfileWidget
              id_prefix="order-create-billing"
              user_id={user?.uid}
              onAdded={(data) => {
                setBilling(data);
                setAddNew(false);
                setCustomers((o) =>
                  o.some((c) => c.id === data.id) ? o : [...o, data],
                );
                setPage(Math.ceil((customers.length + 1) / 2));
              }}
            />
          </div>
        </ModalDialog>
      )}

      {customers.length > 0 && (
        <div className="w-full min-w-0">
          {/* Mobile View */}
          <div className="md:hidden min-w-0">
            <div
              className="
                flex
                flex-nowrap
                gap-3
                w-full
                max-w-full
                overflow-x-auto
                snap-x
                snap-mandatory
                px-2
                pb-2
              "
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {customers.map((customer, i) => (
                <div
                  key={i}
                  id={`order-create-billing-card-mobile-${i}`}
                  style={{ flex: "0 0 85%" }}
                  className={`
              snap-start
              bg-white
              rounded-xl
              border
              p-3
              shadow-sm
              cursor-pointer
              ${
                billing?.id === customer.id
                  ? "border-green-400 bg-green-50"
                  : "border-gray-200"
              }
            `}
                  onClick={() => {
                    if (billing?.id === customer.id) {
                      setBilling(undefined);
                    } else {
                      setBilling(customer);
                    }
                    setEdit(false);
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <input
                      id={`order-create-billing-radio-mobile-${i}`}
                      type="radio"
                      checked={billing?.id === customer.id}
                      readOnly
                      className="w-5 h-5"
                      style={{ accentColor: "green" }}
                    />

                    <span className="rounded-full px-3 py-1 text-sm bg-green-50 text-teal-600">
                      {customer.address.type}
                    </span>
                  </div>

                  <div className="font-semibold text-base break-words">
                    {customer.firstName}
                    {customer.branch ? `: ${customer.branch}` : ""}
                  </div>

                  <div className="mt-2 text-sm break-words">
                    {[customer.mobile, customer.alt_mobile]
                      .filter(Boolean)
                      .join(", ")}
                  </div>

                  <div className="text-sm text-gray-500 break-words">
                    {customer.email}
                  </div>

                  <hr className="my-3" />

                  <div className="text-sm">
                    <div>
                      <span className="font-semibold">Phone:</span>{" "}
                      {customer.mobile ?? "--"}
                    </div>
                    <div>
                      <span className="font-semibold">GST Number:</span>{" "}
                      {gstNumber}
                    </div>
                    <div>
                      <span className="font-semibold">GST Details:</span>{" "}
                      {gstDetails}
                    </div>
                    <div>
                      <span className="font-semibold">CA:</span>{" "}
                      {customer.ca ?? "--"}
                    </div>
                    <div>
                      <span className="font-semibold">Customer Group:</span>{" "}
                      {customerGroup}
                    </div>
                    <div>
                      <span className="font-semibold">Customer Type:</span>{" "}
                      {customerType}
                    </div>
                  </div>

                  <hr className="my-3" />

                  <div className="text-sm text-gray-500 break-words">
                    {[
                      customer.address.line1,
                      customer.address.line2,
                      customer.address.city,
                      customer.address.state,
                      customer.address.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {customer.address.pincode &&
                      ` - ${customer.address.pincode}`}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 w-full">
              {customers.map((customer, i) => (
                      <div
                        id={`order-create-billing-card-${i}`}
                        className={`cursor-pointer bg-white hover:bg-green-50 min-h-[240px] w-full p-5 rounded-lg border ${
                          billing?.id === customer.id
                            ? "border-green-400"
                            : "border-gray-200"
                        }`}
                        key={i}
                        onClick={() => {
                          if (billing?.id === customer.id) {
                            setBilling(undefined);
                          } else {
                            setBilling(customer);
                          }
                          setEdit(false);
                        }}
                      >
                        <div className="flex gap-1 justify-between mb-2">
                          <input
                            style={{ accentColor: "green" }}
                            className="w-5 h-5"
                            type="radio"
                            name=""
                            id={`order-create-billing-radio-${i}`}
                            checked={billing?.id === customer.id}
                          />

                          <p className="  rounded-3xl px-3 py-1   text-teal bg-green-50">
                            {customer.address.type}
                          </p>
                        </div>
                        <div className="flex gap-2 my-1">
                          <p>
                            <b>
                              {customer.firstName}: {customer.branch}
                            </b>
                          </p>
                        </div>
                        <div className="flex gap-1 my-1">
                          <p>
                            {customer.mobile} , {customer.alt_mobile}
                          </p>
                        </div>
                        <div className="flex gap-1 my-1">
                          <p>{customer.email}</p>
                        </div>
                        <hr className="my-2" />
                        <div className="flex gap-1">
                          <span className="font-semibold">Phone :</span>
                          <p>{customer.mobile ?? "--"}</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="font-semibold">GST Number :</span>
                          <p>{gstNumber}</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="font-semibold">GST Details :</span>
                          <p>{gstDetails}</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="font-semibold">CA :</span>
                          <p>{customer.ca ?? "--"}</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="font-semibold">
                            Customer Group :
                          </span>
                          <p>{customerGroup}</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="font-semibold">Customer Type :</span>
                          <p>{customerType}</p>
                        </div>
                        <hr className="my-2" />
                        <div className="flex gap-1 text-xs text-gray-500">
                          <p className="break-words">
                            {[
                              customer.address.line1,
                              customer.address.line2,
                              customer.address.city,
                              customer.address.state,
                              customer.address.country,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                            {customer.address.pincode
                              ? ` - ${customer.address.pincode}`
                              : ""}
                          </p>
                        </div>
                      </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewBillingProfileWidget;
